import { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'features/auth/slice';

import useFetch from 'lib/hooks/useFetch';
import useReports from 'lib/hooks/useReports';

import {
  Button,
  Card,
  flash,
  Icon,
  LinkWithButtonStyles,
  recordsMatch,
  Tabs,
  useModal,
} from '@frontendmentorio/design-system-v2';

import AccessibilityModal from 'features/solutions/Show/Reports/AccessibilityModal';
import CSSModal from 'features/solutions/Show/Reports/CSSModal';
import HTMLModal from 'features/solutions/Show/Reports/HTMLModal';
import JSModal from 'features/solutions/Show/Reports/JSModal';
import Issues from 'features/solutions/Show/Reports/Issues';
import EmptyState from 'features/solutions/Show/Reports/EmptyState';
import Summary from 'features/solutions/Show/Reports/Summary';
import Strengths from 'features/solutions/Show/Reports/Strengths';

const isProcessingIssue = (report) => {
  if (!report) return false;

  return report.issues?.some((issue) => issue.recommendation === 'processing');
};

export default function Reports({ solution }) {
  const currentUser = useSelector(selectCurrentUser);
  const isAuthor = recordsMatch(currentUser, solution?.user);
  const [activeTab, setActiveTab] = useState('accessibility');
  const { generate, recommend, watch, unwatch } = useReports();

  const accessibilityModal = useModal();
  const cssModal = useModal();
  const htmlModal = useModal();
  const jsModal = useModal();

  const MODALS = {
    accessibility: accessibilityModal,
    CSS: cssModal,
    HTML: htmlModal,
    JavaScript: jsModal,
  };

  const {
    data: { data: report },
  } = useFetch(`/api/v3/solutions/${solution.id}/report`);

  const activeReport = useMemo(
    () => report?.[activeTab.toLowerCase()],
    [report, activeTab]
  );

  const isProcessing = useMemo(() => {
    if (report?.status === 'processing') return true;
    if (isProcessingIssue(report?.accessibility)) return true;
    if (isProcessingIssue(report?.html)) return true;
    if (isProcessingIssue(report?.css)) return true;
    if (isProcessingIssue(report?.javascript)) return true;

    return false;
  }, [report]);

  const handleGenerate = async () => {
    try {
      await generate(solution);
    } catch (error) {
      flash.error(error.message);
    }
  };

  const handleRecommend = async (issue) => {
    try {
      await recommend(report, activeTab.toLowerCase(), issue);
    } catch (error) {
      flash.error(error.message);
    }
  };

  useEffect(() => {
    if (isProcessing) return watch(solution);

    unwatch(solution);
  }, [report]);

  return (
    <div id="reports" className="flex flex-col gap-4 w-full">
      <Tabs
        activeTab={activeTab}
        ariaLabel="Select report type"
        onChange={setActiveTab}
        options={[
          {
            label: 'Accessibility report',
            value: 'accessibility',
          },
          {
            label: 'HTML report',
            value: 'HTML',
          },
          {
            label: 'CSS report',
            value: 'CSS',
          },
          {
            label: 'JavaScript report',
            value: 'JavaScript',
          },
        ]}
        tabsClassName="self-start"
      />

      <span className="bg-blue-100 flex gap-2 items-center rounded-full px-3 py-2 self-start text-blue-500 text-sm">
        <Icon name="eye" />
        <span>Reports are only visible to you</span>
      </span>

      <Card className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-4 items-center text-center tablet:flex-row tablet:justify-between tablet:text-start">
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Improved your solution?</span>

            <span className="text-neutral-500">
              Generate a new report to resolve errors and warnings.
            </span>
          </div>

          <div className="flex flex-col gap-2 items-center desktop:items-end">
            <Button
              disabled={report?.status === 'processing'}
              onClick={handleGenerate}
            >
              {report?.status === 'processing'
                ? 'Processing...'
                : 'Regenerate reports'}
            </Button>

            <button
              className="text-start text-wrap tablet:ml-auto text-sm underline hover:no-underline"
              onClick={MODALS[activeTab].open}
            >
              Learn more about our {activeTab} report
            </button>
          </div>
        </div>
      </Card>

      {!currentUser?.hasActiveSubscription && !report?.withAI && (
        <div className="bg-violet-50 border border-violet-200 flex flex-col gap-4 items-start justify-between p-3 rounded tablet:flex-row tablet:items-center tablet:gap-6">
          <div className="flex flex-col gap-3 max-w-[56rem]">
            <h2 className="text-violet-600 font-secondary text-xl">
              Unlock AI-powered analysis
            </h2>
            <p>
              Upgrade to Pro to get AI analysis that can{' '}
              <strong>detect up to 3x more issues</strong> including code
              quality problems, best practice violations, and optimization
              opportunities that standard tools miss.
            </p>
          </div>
          <LinkWithButtonStyles
            className="bg-violet-600 border-violet-600 flex-shrink-0 hover:bg-violet-800 hover:border-violet-800"
            href="/pro#plans"
          >
            Upgrade now
          </LinkWithButtonStyles>
        </div>
      )}

      {activeReport?.summary && <Summary report={activeReport} isOpen />}

      {!activeReport?.issues?.length ? (
        <EmptyState
          report={activeReport}
          solution={solution}
          status={report?.status}
          type={activeTab}
        />
      ) : (
        <Issues
          currentUser={currentUser}
          isAuthor={isAuthor}
          isOpen={!activeReport?.summary}
          onClick={handleRecommend}
          report={activeReport}
          type={activeTab}
        />
      )}

      {activeReport?.strengths && <Strengths report={activeReport} />}

      <AccessibilityModal modal={accessibilityModal} />
      <CSSModal modal={cssModal} />
      <HTMLModal modal={htmlModal} />
      <JSModal modal={jsModal} />
    </div>
  );
}
